import { NIGHTMARES, SENDA, sec } from '@somnhold/content';
import { ANGLE_STEPS, dist2, iatan2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, GuardRules, Hero, MonasteryRules, MonasteryState, SendaNpc } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { guardTick } from './keep.ts';
import { sendaCue } from './senda.ts';
import { CHAPEL_HALF, CHAPEL_S, CISTERN_R, CISTERN_Y, CORR_X, GALLERY_HALF, GALLERY_Y, PATIO_N, monRegion, type MonasteryLayout } from './monastery-layout.ts';

/**
 * The monastery of the waterfall (docs/sendas/paladin.md, chapter 4; §4, «Las huellas» and «El monasterio»), on one
 * board (battle/monastery-layout.ts) for its three acts:
 *
 * - The memory (`porch`, act 1): years before the fall, played as Marit at seventeen, the squire. A small Marea comes up
 *   the gorge and the monastery shuts its door; Ser Edda fights on its threshold and never falls (a memory). The squire
 *   within `cover` of her takes `coverBp` of a blow (`monCover`, from combat's damage on a hero): «ponerse en medio» her
 *   way. Gone past `far` a while, Edda calls her back; at `blowAt` she takes a blow meant for the squire. Won when the
 *   Marea has passed (the fight's clock).
 * - The cells (`cells`, act 2): something puts out the fifth altar: spectres no one sees, a group every `every` out of
 *   one of eight cells, along the corridors to the chapel. A handful of salt thrown on a spot of a corridor shows what
 *   walks through it (`monSeen`, read by combat's `isRevealed`) and leaves its prints, pointing the way they went. Four
 *   handfuls; the refectory's sacks give four more (a long way back). A line of salt at a cell's door shuts it (one door
 *   at a time); shut the right one and its spectres fade. At the altar, a spectre puts it out and stays on Marit a
 *   while; she lights it again. Won with the right door shut and the altar burning.
 * - The cloister (`cloister`, act 3, the test): the sixth altar at the bottom of the cistern the waterfall fills (a mark
 *   every `fall`). Three gates, each a mark (or three) every `pulse` while open: the orchards (they fill and shut
 *   themselves), the kitchen (its canal runs under the chapel: the fourth mark out by it puts the fifth altar out, and
 *   each third after; twice loses it), the ravine (three, but opening it the first time brings the great sentinel up 20 s
 *   later; else at five minutes). The siege comes over the patio's wall in bands, goes quiet, and comes back. With the
 *   water at `bare` or less Marit lights the sixth altar; won with it lit and the great sentinel down.
 *
 * Cost: the people are a handful; the spectres, the seen and the prints short sorted lists; a nightmare's way round a
 * wall is a look at its region, not a search (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

/** Half a turn (the angle's steps). */
const ANGLE_HALF = ANGLE_STEPS >> 1;
const CHAPEL_DOOR_HALF = 700;

/** The monastery's rules from its layout (the fight's builder, campaign/sendas.ts; and the renderer's scenes). */
export const monasteryRules = (L: MonasteryLayout, kind: MonasteryRules['kind'], marit: GuardRules, edda: GuardRules, bossHp: number): MonasteryRules => {
  const M = SENDA.monastery;
  const keeps =
    kind === 'porch'
      ? [{ id: 'edda', x: L.keeps.edda!.x, y: L.keeps.edda!.y }]
      : kind === 'cells'
        ? [
            { id: 'marit', x: L.keeps.marit!.x, y: L.keeps.marit!.y },
            { id: 'tadeo', x: L.keeps.tadeo!.x, y: L.keeps.tadeo!.y },
          ]
        : [
            { id: 'marit', x: L.keeps.maritCloister!.x, y: L.keeps.maritCloister!.y },
            { id: 'tadeo', x: L.keeps.tadeoChapel!.x, y: L.keeps.tadeoChapel!.y },
          ];
  const base: MonasteryRules = { kind, keeps, marit, turn: M.lever.turn, reach: M.lever.reach };
  if (kind === 'porch') {
    const P = M.porch;
    const at: number[] = [];
    const n: number[] = [];
    for (let t = P.first; t < P.ticks - P.every; t += P.every) {
      n.push(imax(1, P.n + (L.foot.more[at.length % L.foot.more.length] as number)));
      at.push(t);
    }
    return {
      ...base,
      porch: { edda, cover: P.cover, coverBp: P.coverBp, far: P.far, callEvery: P.callEvery, blowAt: P.blowAt, bands: { at, n, kinds: P.kinds.slice(), footY: L.foot.y, xs: L.foot.xs.slice() } },
    };
  }
  if (kind === 'cells') {
    const C = M.cells;
    return {
      ...base,
      cells: {
        doors: L.cells.map((c) => ({ x: c.x, y: c.y })),
        culprit: L.culprit,
        routes: L.routes.map((r) => r.slice()),
        spots: L.spots.map((p) => ({ x: p.x, y: p.y })),
        radius: C.radius,
        handfuls: C.handfuls,
        sack: { x: L.sack.x, y: L.sack.y },
        fetch: C.fetch,
        prints: C.prints,
        seen: C.seen,
        first: C.first,
        every: C.every,
        n: C.n,
        pace: C.pace,
        linger: C.linger,
        relight: C.relight,
        altar: { x: L.altar5.x, y: L.altar5.y },
      },
    };
  }
  const K = M.cloister;
  const at: number[] = [];
  for (let t = sec(L.siege.first); t < sec(L.siege.quiet); t += K.siege.every) at.push(t);
  for (let t = sec(L.siege.back); t < sec(L.siege.back) + K.siege.every * K.siege.back; t += K.siege.every) at.push(t);
  return {
    ...base,
    cloister: {
      cap: K.cap,
      start: K.start,
      bare: K.bare,
      fall: K.fall,
      pulse: K.pulse,
      gates: L.gates.map((g) => ({ x: g.x, y: g.y })),
      take: K.take.slice(),
      orchards: L.orchards,
      snuffAt: K.snuffAt,
      snuffEvery: K.snuffEvery,
      snuffs: K.snuffs,
      reflect: K.reflect,
      light: K.light,
      altar6: { x: L.altar6.x, y: L.altar6.y },
      boss: { hp: bossHp, rise: K.boss.rise, late: K.boss.late, climb: L.climb.slice() },
      siege: { at, every: K.siege.every, n: K.siege.n, kinds: K.siege.kinds.slice(), y: K.siege.y, xs: K.siege.xs.slice() },
    },
  };
};

/** The monastery as a fight starts (the renderer's scenes build one too). */
export const monasteryFresh = (r: MonasteryRules, npcs = 0): MonasteryState => ({
  swing: new Array<number>(npcs).fill(0),
  band: 0,
  farT: -1,
  callT: -100000,
  blow: false,
  job: -1,
  held: 0,
  salt: r.cells?.handfuls ?? 0,
  salted: new Array<number>(r.cells?.spots.length ?? 0).fill(0),
  prints: [],
  shut: -1,
  ghostId: [],
  ghostLeg: [],
  ghostStay: [],
  seenId: [],
  seenUntil: [],
  outAt: -1,
  outs: 0,
  level: r.cloister?.start ?? 0,
  acc: 0,
  open: [0, 0, 0],
  next: [0, 0, 0],
  out: [0, 0, 0],
  snuffs: 0,
  bossAt: -1,
  bossId: 0,
  bossLeg: 0,
  lightHeld: 0,
  lit: false,
  reflected: false,
  done: false,
});

/** Index of a sorted list's value (−1: not in it). */
const find = (list: readonly number[], v: number): number => {
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = list[m] as number;
    if (x === v) return m;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return -1;
};

/** Where a value goes in a sorted list. */
const slot = (list: readonly number[], v: number): number => {
  let i = 0;
  while (i < list.length && (list[i] as number) < v) i++;
  return i;
};

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/* ---------- the memory: Ser Edda's cover ---------- */

/** A blow on the squire within Ser Edda's cover (combat's damage on a hero): `coverBp` of it. */
export const monCover = (s: BattleState, h: Hero, dmg: number): number => {
  const P = s.rules?.senda?.monastery?.porch;
  if (!P) return dmg;
  const e = npcOf(s, 'edda');
  if (!e || !withinRange(h.x, h.y, e.x, e.y, P.cover)) return dmg;
  return mulBp(dmg, P.coverBp);
};

/** Whether the squire stands by Ser Edda now (the HUD's mark, the bot). */
export const monCovered = (s: BattleState, h: Hero): boolean => {
  const P = s.rules?.senda?.monastery?.porch;
  const e = npcOf(s, 'edda');
  return !!P && !!e && withinRange(h.x, h.y, e.x, e.y, P.cover);
};

/* ---------- the cells: who is seen ---------- */

/** Whether a spectre walks through a salted spot now (the spot, −1: none). */
const saltedSpot = (r: NonNullable<MonasteryRules['cells']>, w: MonasteryState, x: number, y: number): number => {
  for (let i = 0; i < r.spots.length; i++) {
    if (!w.salted[i]) continue;
    const p = r.spots[i] as { x: number; y: number };
    if (withinRange(x, y, p.x, p.y, r.radius)) return i;
  }
  return -1;
};

/** Whether a nightmare shows (combat's `isRevealed`): the cells' spectres only in salt, or a while after it. */
export const monSeen = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.monastery?.cells;
  const w = s.senda?.monastery;
  if (!r || !w || find(w.ghostId, e.id) < 0) return !NIGHTMARES[e.kind].invisible;
  const i = find(w.seenId, e.id);
  if (i >= 0 && (w.seenUntil[i] as number) > s.tick) return true;
  return saltedSpot(r, w, e.x, e.y) >= 0;
};

/** The points a job sends the hero to: the cells' spots, the doors (from the corridor), the sacks; the cloister's gates. */
export const monJobAt = (r: MonasteryRules, at: number): { x: number; y: number } | null => {
  const C = r.cells;
  if (C) {
    if (at >= 0 && at < C.spots.length) return C.spots[at] as { x: number; y: number };
    const d = at - C.spots.length;
    if (d >= 0 && d < C.doors.length) {
      const door = C.doors[d] as { x: number; y: number };
      return { x: door.x < 0 ? -CORR_X - 300 : CORR_X + 300, y: door.y };
    }
    if (d === C.doors.length) return C.sack;
    return null;
  }
  const K = r.cloister;
  if (K && at >= 0 && at < K.gates.length) return K.gates[at] as { x: number; y: number };
  return null;
};

/** The hero sent to a job: a handful on a spot, a door's line, the sacks (the cells); a gate (the cloister). */
export const monOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.monastery;
  const w = s.senda?.monastery;
  if (!r || !w) return;
  const p = monJobAt(r, at);
  if (!p) return;
  w.job = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/* ---------- a way round the walls ---------- */

/** Whether the straight way from a to b passes through the cistern (with a margin). */
const crossesCistern = (ax: number, ay: number, bx: number, by: number): boolean => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const cy = CISTERN_Y;
  let t = len2 > 0 ? idiv(((0 - ax) * dx + (cy - ay) * dy) * 1000, len2) : 0;
  t = imax(0, imin(1000, t));
  const px = ax + idiv(dx * t, 1000);
  const py = ay + idiv(dy * t, 1000);
  const m = CISTERN_R + 450;
  return px * px + (py - cy) * (py - cy) < m * m;
};

/**
 * The next point on a nightmare's way to a target when the straight way meets a wall (null: it doesn't): the chapel's
 * door, a crossing, the cistern's side. A look at the regions, not a search.
 */
export const monVia = (x: number, y: number, tx: number, ty: number): { x: number; y: number } | null => {
  const a = monRegion(x, y);
  const b = monRegion(tx, ty);
  const door = { x: 0, y: CHAPEL_S + 500 };
  const inChapel = (rx: number, ry: number): boolean => ry <= CHAPEL_S + 1 && Math.abs(rx) <= CHAPEL_HALF;
  // In or out of the chapel: by its door (and to the door from the gallery's line).
  if (inChapel(tx, ty) !== inChapel(x, y)) {
    if (inChapel(x, y)) return withinRange(x, y, door.x, door.y, 300) ? { x: 0, y: GALLERY_Y } : door;
    if (Math.abs(x) > CHAPEL_DOOR_HALF && y < GALLERY_Y - GALLERY_HALF + 200) return { x: 0, y: GALLERY_Y };
    if (y > GALLERY_Y + GALLERY_HALF && crossesCistern(x, y, door.x, door.y)) return { x: x < 0 ? -CISTERN_R - 900 : CISTERN_R + 900, y: CISTERN_Y };
    return door;
  }
  // Up a corridor's north arm: to its crossing first.
  if (a === 2 && y < GALLERY_Y - GALLERY_HALF && !(b === 2 && ty < GALLERY_Y - GALLERY_HALF && (tx < 0) === (x < 0))) return { x: x < 0 ? -CORR_X : CORR_X, y: GALLERY_Y };
  if (b === 2 && ty < GALLERY_Y - GALLERY_HALF && !(a === 2 && y < GALLERY_Y - GALLERY_HALF)) {
    const cx = tx < 0 ? -CORR_X : CORR_X;
    if (!withinRange(x, y, cx, GALLERY_Y, 500)) return { x: cx, y: GALLERY_Y };
  }
  // Round the cistern.
  if (y < PATIO_N + 2000 && crossesCistern(x, y, tx, ty)) {
    const side = x + tx < 0 ? -1 : 1;
    return { x: side * (CISTERN_R + 900), y: CISTERN_Y };
  }
  return null;
};
/** The nearest the nightmare can go for: the standing hero, or a person of the path not kept safe. */
const nearestFoe = (s: BattleState, e: Enemy): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || h.light !== undefined) continue;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      best = h;
    }
  }
  const st = s.senda;
  const r = s.rules?.senda;
  if (st && r)
    for (let i = 0; i < st.npcs.length; i++) {
      const n = st.npcs[i] as SendaNpc;
      if (n.hp <= 0 || n.away || r.npcs[i]?.safe) continue;
      const d = dist2(e.x, e.y, n.x, n.y);
      if (d < bd) {
        bd = d;
        best = n;
      }
    }
  return best;
};

/* ---------- the bands ---------- */

const spawnAt = (s: BattleState, kind: string, x: number, y: number, face: number): Enemy => {
  const e = spawnEnemy(s, kind as Enemy['kind'], 1, false, 0, x, y);
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  e.facing = face;
  e.anim = 'walk';
  return e;
};

/* ---------- each act's tick ---------- */

const porchTick = (s: BattleState, r: MonasteryRules, w: MonasteryState, cue: (c: string) => void): void => {
  const P = r.porch as NonNullable<MonasteryRules['porch']>;
  const T = s.waveT;
  if (w.band < P.bands.at.length && T >= (P.bands.at[w.band] as number)) {
    for (let k = 0; k < (P.bands.n[w.band] as number); k++) {
      const x = P.bands.xs[(w.band + k) % P.bands.xs.length] as number;
      spawnAt(s, P.bands.kinds[(w.band + k) % P.bands.kinds.length] as string, x, P.bands.footY - (k >> 1) * 500, iatan2(-1, 0));
    }
    w.band++;
  }
  const st = s.senda;
  const edda = npcOf(s, 'edda');
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!st || !edda) return;
  // A memory: she doesn't fall.
  if (edda.hp < 2) edda.hp = 2;
  const i = st.npcs.indexOf(edda);
  const keep = r.keeps[0] as { x: number; y: number };
  w.swing[i] = guardTick(s, edda, keep, P.edda, w.swing[i] ?? 0);
  if (!h) return;
  // Gone from her side a while: she calls the squire back.
  if (!withinRange(h.x, h.y, edda.x, edda.y, P.far)) {
    if (w.farT < 0) w.farT = s.tick;
    else if (s.tick - w.farT > sec(1) && s.tick - w.callT >= P.callEvery) {
      w.callT = s.tick;
      ev(s, { t: 'senda', kind: 'call', npc: 'edda', x: edda.x, y: edda.y });
      cue('call');
    }
  } else w.farT = -1;
  // The blow meant for the squire, taken by Edda (a short moment, the fight goes on).
  if (!w.blow && T >= P.blowAt) {
    w.blow = true;
    ev(s, { t: 'senda', kind: 'cover', npc: 'edda', x: h.x, y: h.y });
    cue('blow');
  }
};

/** A print in the salt of a spot (five numbers a print, the last 48 kept). */
const print = (w: MonasteryState, spot: number, e: Enemy, tick: number): void => {
  w.prints.push(spot, e.x, e.y, e.facing, tick);
  if (w.prints.length > 240) w.prints.splice(0, w.prints.length - 240);
};

const cellsTick = (s: BattleState, r: MonasteryRules, w: MonasteryState, cue: (c: string) => void): void => {
  const C = r.cells as NonNullable<MonasteryRules['cells']>;
  const T = s.waveT;
  const marit = npcOf(s, 'marit');
  // A group out of the cell, unless its door is shut.
  const at = C.first + w.band * C.every;
  if (T >= at) {
    w.band++;
    if (w.shut !== C.culprit) {
      const door = C.doors[C.culprit] as { x: number; y: number };
      for (let k = 0; k < C.n; k++) {
        const e = spawnAt(s, 'wraith', door.x + (door.x < 0 ? 300 : -300), door.y + k * 350, door.x < 0 ? 0 : ANGLE_HALF);
        const j = slot(w.ghostId, e.id);
        w.ghostId.splice(j, 0, e.id);
        w.ghostLeg.splice(j, 0, 0);
        w.ghostStay.splice(j, 0, 0);
      }
      if (w.band === 1) cue('first');
    }
  }
  // The fifth altar lit again by Marit.
  if (w.outAt >= 0 && s.tick - w.outAt >= C.relight && marit && marit.hp > 0) {
    w.outAt = -1;
    ev(s, { t: 'senda', kind: 'altar', npc: 'marit', x: C.altar.x, y: C.altar.y, n: 0 });
  }
  if (w.shut === C.culprit && w.outAt < 0 && !w.done) {
    w.done = true;
    cue('lit');
  }
  // Marit keeps the altar; she strikes only what she sees.
  if (marit && s.senda) {
    const i = s.senda.npcs.indexOf(marit);
    w.swing[i] = guardTick(s, marit, r.keeps[0] as { x: number; y: number }, r.marit, w.swing[i] ?? 0, (e) => !monSeen(s, e));
  }
  // The dead and the faded off the lists now and then.
  if (s.tick % 24 === 0) {
    for (let i = w.ghostId.length - 1; i >= 0; i--) {
      const id = w.ghostId[i] as number;
      if (s.enemies.some((e) => e.id === id && e.hp > 0)) continue;
      w.ghostId.splice(i, 1);
      w.ghostLeg.splice(i, 1);
      w.ghostStay.splice(i, 1);
    }
    for (let i = w.seenId.length - 1; i >= 0; i--) {
      if ((w.seenUntil[i] as number) > s.tick) continue;
      w.seenId.splice(i, 1);
      w.seenUntil.splice(i, 1);
    }
  }
};

/** A spectre along its cell's way; at the altar it puts it out and stays on Marit a while, then fades. */
const ghostMove = (s: BattleState, r: NonNullable<MonasteryRules['cells']>, w: MonasteryState, e: Enemy, gi: number, cue: (c: string) => void): void => {
  const stay = w.ghostStay[gi] as number;
  if (stay > 0) {
    e.anim = 'attack';
    if (s.tick >= stay) {
      e.hp = 0;
      ev(s, { t: 'senda', kind: 'fade', npc: '', x: e.x, y: e.y });
    }
    return;
  }
  const route = r.routes[r.culprit] as number[];
  const leg = w.ghostLeg[gi] as number;
  const tx = route[leg * 2] as number;
  const ty = route[leg * 2 + 1] as number;
  e.anim = 'walk';
  // Through the salt: seen, and its prints left behind.
  const sp = saltedSpot(r, w, e.x, e.y);
  if (sp >= 0) {
    const i = find(w.seenId, e.id);
    const until = s.tick + r.seen;
    if (i >= 0) w.seenUntil[i] = until;
    else {
      const j = slot(w.seenId, e.id);
      w.seenId.splice(j, 0, e.id);
      w.seenUntil.splice(j, 0, until);
    }
    if (s.tick % 4 === 0) print(w, sp, e, s.tick);
  }
  if (stepToward(e, tx, ty, r.pace)) {
    if ((leg + 1) * 2 < route.length) w.ghostLeg[gi] = leg + 1;
    else {
      w.ghostStay[gi] = s.tick + r.linger;
      if (w.outAt < 0) {
        w.outAt = s.tick;
        w.outs++;
        ev(s, { t: 'senda', kind: 'altar', npc: '', x: r.altar.x, y: r.altar.y, n: 1 });
        cue('out');
      }
    }
  }
};

/** A handful, a door's line, the sacks (the cells); a gate turned (the cloister): the job done standing still by it. */
const work = (s: BattleState, r: MonasteryRules, w: MonasteryState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const p = monJobAt(r, w.job);
  if (!p) {
    w.job = -1;
    return;
  }
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
    w.held = 0;
    if (!h.moving && !withinRange(h.x, h.y, p.x, p.y, r.reach)) w.job = -1;
    return;
  }
  const C = r.cells;
  const need = C && w.job === C.spots.length + C.doors.length ? C.fetch : r.turn;
  if (++w.held < need) return;
  const j = w.job;
  w.job = -1;
  w.held = 0;
  if (C) {
    if (j < C.spots.length) {
      if (w.salt <= 0 || w.salted[j]) {
        ev(s, { t: 'senda', kind: 'stuck', npc: '', x: p.x, y: p.y, n: j });
        return;
      }
      w.salt--;
      w.salted[j] = s.tick;
      ev(s, { t: 'senda', kind: 'salt', npc: '', x: p.x, y: p.y, n: j });
      cue('salt');
      return;
    }
    const d = j - C.spots.length;
    if (d < C.doors.length) {
      w.shut = d;
      ev(s, { t: 'senda', kind: 'shut', npc: '', x: p.x, y: p.y, n: d });
      if (d === C.culprit) {
        // Its spectres fade: they came from it.
        for (const id of w.ghostId) {
          const e = s.enemies.find((x) => x.id === id && x.hp > 0);
          if (!e) continue;
          e.hp = 0;
          ev(s, { t: 'senda', kind: 'fade', npc: '', x: e.x, y: e.y });
        }
        cue('shut');
      } else cue('wrong');
      return;
    }
    w.salt = C.handfuls;
    ev(s, { t: 'senda', kind: 'fetch', npc: '', x: p.x, y: p.y, n: w.salt });
    return;
  }
  const K = r.cloister;
  if (!K || j >= K.gates.length) return;
  // The orchards, full, stay shut.
  if (j === 0 && w.out[0]! >= K.orchards) {
    ev(s, { t: 'senda', kind: 'stuck', npc: '', x: p.x, y: p.y, n: j });
    return;
  }
  w.open[j] = w.open[j] ? 0 : 1;
  w.next[j] = K.pulse;
  ev(s, { t: 'senda', kind: 'gate', npc: '', x: p.x, y: p.y, n: j * 2 + (w.open[j] as number) });
  // The ravine opened the first time: the great sentinel comes up from it.
  if (j === 2 && w.open[2] && (w.bossAt < 0 || w.bossAt > s.tick + K.boss.rise) && w.bossId === 0) {
    w.bossAt = s.tick + K.boss.rise;
    cue('ravine');
  }
};

const cloisterTick = (s: BattleState, r: MonasteryRules, w: MonasteryState, cue: (c: string) => void): void => {
  const K = r.cloister as NonNullable<MonasteryRules['cloister']>;
  const T = s.waveT;
  // The waterfall, always.
  if (++w.acc >= K.fall) {
    w.acc = 0;
    if (w.level < K.cap) {
      w.level++;
      ev(s, { t: 'senda', kind: 'mark', npc: '', x: K.altar6.x, y: K.altar6.y, n: w.level });
    }
  }
  // The gates, a pulse at a time.
  for (let g = 0; g < 3; g++) {
    if (!w.open[g]) continue;
    w.next[g] = (w.next[g] as number) - 1;
    if ((w.next[g] as number) > 0) continue;
    w.next[g] = K.pulse;
    let take = imin(K.take[g] as number, w.level);
    if (g === 0) take = imin(take, K.orchards - (w.out[0] as number));
    if (take > 0) {
      w.level -= take;
      ev(s, { t: 'senda', kind: 'pour', npc: '', x: (K.gates[g] as { x: number }).x, y: (K.gates[g] as { y: number }).y, n: g });
      ev(s, { t: 'senda', kind: 'mark', npc: '', x: K.altar6.x, y: K.altar6.y, n: w.level });
    }
    for (let k = 0; k < take; k++) {
      w.out[g] = (w.out[g] as number) + 1;
      // The kitchen's canal under the chapel: the fourth mark out by it, and each third after, on the fifth altar.
      const m = w.out[g] as number;
      if (g === 1 && m >= K.snuffAt && (m - K.snuffAt) % K.snuffEvery === 0) {
        w.snuffs++;
        ev(s, { t: 'senda', kind: 'snuff', npc: '', x: 0, y: -8700, n: w.snuffs });
        cue(`snuff${w.snuffs}`);
      }
    }
    if (g === 0 && (w.out[0] as number) >= K.orchards) {
      w.open[0] = 0;
      ev(s, { t: 'senda', kind: 'gate', npc: '', x: (K.gates[0] as { x: number }).x, y: (K.gates[0] as { y: number }).y, n: 0 });
      cue('orchards');
    }
  }
  if (!w.reflected && w.level <= K.reflect) {
    w.reflected = true;
    ev(s, { t: 'senda', kind: 'reflect', npc: 'fled', x: K.altar6.x, y: K.altar6.y });
    cue('reflect');
  }
  // Marit lights the sixth altar with the water low enough.
  const marit = npcOf(s, 'marit');
  if (!w.lit && marit && marit.hp > 0) {
    if (w.level <= K.bare) {
      if (++w.lightHeld >= K.light) {
        w.lit = true;
        ev(s, { t: 'senda', kind: 'lit', npc: 'marit', x: K.altar6.x, y: K.altar6.y });
        cue('lit');
      }
    } else w.lightHeld = 0;
  }
  // The siege over the patio's wall.
  if (w.band < K.siege.at.length && T >= (K.siege.at[w.band] as number)) {
    for (let k = 0; k < K.siege.n; k++) spawnAt(s, K.siege.kinds[(w.band + k) % K.siege.kinds.length] as string, K.siege.xs[(w.band + k) % K.siege.xs.length] as number, K.siege.y, iatan2(-1, 0));
    if (w.band === 0) cue('siege');
    w.band++;
  }
  // The great sentinel: late, if the ravine never opened.
  if (w.bossAt < 0 && T >= K.boss.late) w.bossAt = s.tick;
  if (w.bossId === 0 && w.bossAt >= 0 && s.tick >= w.bossAt) {
    const e = spawnEnemy(s, 'sentinel', 1, true, 0, K.boss.climb[0] as number, K.boss.climb[1] as number, K.boss.hp);
    e.facing = ANGLE_HALF;
    e.anim = 'walk';
    w.bossId = e.id;
    w.bossLeg = 1;
    s.bossId = e.id;
    ev(s, { t: 'senda', kind: 'rise', npc: '', x: e.x, y: e.y });
    cue('rise');
  } else if (w.bossId > 0 && !s.enemies.some((e) => e.id === w.bossId && e.hp > 0)) {
    w.bossId = -1;
    cue('felled');
  }
  // The people keep their places: Marit fights at the cistern; Tadeo, by the fifth altar, only takes.
  if (marit && s.senda) {
    const i = s.senda.npcs.indexOf(marit);
    w.swing[i] = guardTick(s, marit, r.keeps[0] as { x: number; y: number }, r.marit, w.swing[i] ?? 0);
  }
};

/** The spectres' ways, the great sentinel's climb, a nightmare's way round the walls (from the enemies' tick). */
export const monasteryHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.monastery;
  const w = s.senda?.monastery;
  if (!r || !w) return false;
  if (r.cells && w.ghostId.length) {
    const gi = find(w.ghostId, e.id);
    if (gi >= 0) {
      ghostMove(s, r.cells, w, e, gi, (c) => sendaCue(s, c));
      return true;
    }
  }
  if (e.stun > 0) return false;
  if (r.cloister && e.id === w.bossId) {
    // Met on its way (a hero this near): it turns on the hero and fights it there; left far behind, it goes on.
    const h = s.heroes.find((x) => x.downTicks === 0);
    if (h && withinRange(h.x, h.y, e.x, e.y, SENDA.monastery.cloister.boss.meet)) {
      e.taunter = h.id;
      e.tauntTicks = sec(3);
      return false;
    }
    const climb = r.cloister.boss.climb;
    if (w.bossLeg * 2 < climb.length) {
      // Up from the ravine, along the corridor, to the chapel: whatever strikes it on the way.
      e.anim = 'walk';
      if (stepToward(e, climb[w.bossLeg * 2] as number, climb[w.bossLeg * 2 + 1] as number, pace(e))) w.bossLeg++;
      return true;
    }
  }
  // Round the walls: to the door, the crossing, the cistern's side first.
  const t = nearestFoe(s, e);
  if (!t) return false;
  const via = monVia(e.x, e.y, t.x, t.y);
  if (!via) return false;
  e.anim = 'walk';
  e.tgtKind = 0;
  e.tgtId = 0;
  stepToward(e, via.x, via.y, pace(e));
  return true;
};

export const monasteryTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.monastery;
  const st = s.senda;
  if (!r || !st) return;
  if (!st.monastery) st.monastery = monasteryFresh(r, st.npcs.length);
  const w = st.monastery;
  if (!s.started) return;
  work(s, r, w, cue);
  if (r.porch) porchTick(s, r, w, cue);
  else if (r.cells) cellsTick(s, r, w, cue);
  else cloisterTick(s, r, w, cue);
};

/** The memory: the Marea's clock; the cells: done or not; the cloister: the water down to bare, then the sentinel. */
export const monasteryGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.monastery;
  const w = s.senda?.monastery;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.porch) return { done: s.waveT, need: s.rules?.senda?.goal.need ?? 1 };
  if (r.cells) return { done: w.done ? 1 : 0, need: 1 };
  const K = r.cloister as NonNullable<MonasteryRules['cloister']>;
  return { done: K.cap - imax(K.bare, w.level), need: K.cap - K.bare };
};

export const monasteryResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.monastery;
  const w = s.senda?.monastery;
  if (!r || !w) return null;
  if (r.porch) return s.waveT >= (s.rules?.senda?.goal.need ?? 0) ? 'won' : null;
  if (r.cells) return w.done ? 'won' : null;
  const K = r.cloister as NonNullable<MonasteryRules['cloister']>;
  if (w.snuffs >= K.snuffs) return 'lost';
  return w.lit && w.bossId === -1 ? 'won' : null;
};

